import { mapStyles } from "./config";

// Map pins, drawn here rather than taken from the Mapbox style's sprite. The
// sprite's pins are baked in colors that aren't part of the app's color system
// and can only be changed in Mapbox Studio; drawing them locally lets every pin
// take its fill straight from mapStyles, so a pin and its legend swatch always
// read from the same constant.
//
// The geometry matches the sprite's school pin: a 29x54 teardrop, a circle of
// r=14.5 with tangents running down to the tip at (14.5, 54).
const PIN_SIZE = { width: 29, height: 54 };
const PIN_PATH = "M14.5 54 L1.0124 19.8228 A14.5 14.5 0 1 1 27.9876 19.8228 Z";
// A thin white outline, so pins stacked on top of each other stay countable
// instead of merging into one silhouette. The stroke straddles the edge of the
// path, so the viewBox is padded by half of it on every side to keep the
// outside half from being clipped. Padding symmetrically leaves the image's
// center — which is what Mapbox anchors the icon by — where it was.
const PIN_STROKE_WIDTH = 2;
const PIN_STROKE_COLOR = mapStyles.schoolLayerStrokeColor;
// Rasterize at 2x and tell Mapbox so, so pins stay crisp on retina displays
// while laying out at the same size as the sprite's.
const PIN_PIXEL_RATIO = 2;

// Pins in use, each id paired with its fill, taken from the top of the legend
// tier ramp.
const PIN_TIERS = [
  ["pin-tier4", mapStyles.legendTier4Color],
  ["pin-tier5", mapStyles.legendTier5Color],
  ["pin-tier6", mapStyles.legendTier6Color],
  ["pin-tier7", mapStyles.legendTier7Color],
];
const PIN_COLORS = Object.fromEntries(PIN_TIERS);

// The pin for schools that aren't being colored by grant fiscal year, and the
// fallback for a fiscal year the ramp below doesn't name.
export const PIN_DEFAULT = "pin-tier4";

// Fiscal year to pin, oldest to newest / darkest to lightest, as specified for
// the grant programs. Every grant map shares this ramp, so a given year reads
// the same color wherever it appears — the PA and MD programs start in
// different years but their overlap matches. Note that the three years spend
// the ramp down to PIN_DEFAULT's tier, so a 2027 cohort has no shade of its own
// left to take and falls back to PIN_DEFAULT until one is chosen for it.
const FISCAL_YEAR_PINS = {
  2024: "pin-tier7",
  2025: "pin-tier6",
  2026: "pin-tier5",
};

export const fiscalYearPin = (year) => FISCAL_YEAR_PINS[year] || PIN_DEFAULT;

// The swatch color for a year's pin, so a legend can't drift from the map.
export const fiscalYearColor = (year) => PIN_COLORS[fiscalYearPin(year)];

// Icon-image expression coloring a symbol layer's pins by fiscal year.
export const fiscalYearMarker = [
  "match",
  ["get", "fiscalYear"],
  ...Object.entries(FISCAL_YEAR_PINS).flat(),
  PIN_DEFAULT,
];

const pinImage = (color) => {
  const pad = PIN_STROKE_WIDTH / 2;
  const width = PIN_SIZE.width + PIN_STROKE_WIDTH;
  const height = PIN_SIZE.height + PIN_STROKE_WIDTH;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${
    width * PIN_PIXEL_RATIO
  }" height="${
    height * PIN_PIXEL_RATIO
  }" viewBox="${-pad} ${-pad} ${width} ${height}"><path d="${PIN_PATH}" fill="${color}" stroke="${PIN_STROKE_COLOR}" stroke-width="${PIN_STROKE_WIDTH}"/></svg>`;
  const image = new Image();
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  return image;
};

// Add the pins to a map's sprite. Decoding is async, so a symbol layer
// referencing one may render iconless for a frame; Mapbox repaints the icons
// in as soon as the image lands. Safe to call again on the same map.
export const registerPinImages = (map) => {
  PIN_TIERS.forEach(([id, color]) => {
    if (map.hasImage(id)) return;
    const image = pinImage(color);
    image.onload = () => {
      if (map.hasImage(id)) return;
      map.addImage(id, image, { pixelRatio: PIN_PIXEL_RATIO });
    };
  });
};
